import { baseApi as api } from '../api'
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    ratingRateUser: build.mutation<RatingRateUserApiResponse, RatingRateUserApiArg>({
      query: (queryArg) => ({
        url: `/api/v1/extensions/rating/rate`,
        method: 'POST',
        body: queryArg.ratingRequest,
      }),
    }),
    ratingDeleteRating: build.mutation<RatingDeleteRatingApiResponse, RatingDeleteRatingApiArg>({
      query: (queryArg) => ({
        url: `/api/v1/extensions/rating/rate/${queryArg.ratingId}`,
        method: 'DELETE',
      }),
    }),
    ratingChangeRatingForUser: build.mutation<
      RatingChangeRatingForUserApiResponse,
      RatingChangeRatingForUserApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/extensions/rating/rate/${queryArg.ratingId}`,
        method: 'PUT',
        body: queryArg.patchRatingRequest,
      }),
    }),
    ratingGetRatingId: build.query<RatingGetRatingIdApiResponse, RatingGetRatingIdApiArg>({
      query: (queryArg) => ({ url: `/api/v1/extensions/rating/rate/${queryArg.ratingId}` }),
    }),
    ratingGetRatingsForUser: build.query<
      RatingGetRatingsForUserApiResponse,
      RatingGetRatingsForUserApiArg
    >({
      query: (queryArg) => ({ url: `/api/v1/extensions/rating/rate/user/${queryArg.username}` }),
    }),
  }),
  overrideExisting: false,
})
export { injectedRtkApi as api }
export type RatingRateUserApiResponse = /** status 201 Successful Response */ CreateRatingResponse
export type RatingRateUserApiArg = {
  ratingRequest: RatingRequest
}
export type RatingDeleteRatingApiResponse = /** status 200 Successful Response */ any
export type RatingDeleteRatingApiArg = {
  ratingId: number
}
export type RatingChangeRatingForUserApiResponse = /** status 200 Successful Response */ any
export type RatingChangeRatingForUserApiArg = {
  ratingId: number
  patchRatingRequest: PatchRatingRequest
}
export type RatingGetRatingIdApiResponse = /** status 200 Successful Response */ UserRating
export type RatingGetRatingIdApiArg = {
  ratingId: number
}
export type RatingGetRatingsForUserApiResponse = /** status 200 Successful Response */ UserRating[]
export type RatingGetRatingsForUserApiArg = {
  username: string
}
export type CreateRatingResponse = {
  id: number
}
export type ValidationError = {
  loc: (string | number)[]
  msg: string
  type: string
}
export type HttpValidationError = {
  detail?: ValidationError[]
}
export type RatingRequest = {
  username: string
  rating: number
  comment?: string | null
}
export type PatchRatingRequest = {
  comment: string
  rating: number
}
export type Pronouns = 0 | 1 | 2 | 3
export type TeacherProfileResponse = {
  bio?: string | null
  experience?: string | null
  tags?: string[] | null
  video_link?: string | null
  teaching_style?: string | null
  price_per_hour?: number | null
  partner?: boolean | null
  is_reviewer?: boolean | null
}
export type LanguageExperience = 0 | 1 | 2 | 3 | 4
export type LanguageProficiency = {
  language: string
  proficiency: LanguageExperience
}
export type UserStatus = 'STUDENT' | 'TEACHER'
export type Permission =
  | 'CREATE_EVENT'
  | 'CREATE_PENGGUIN_EVENT'
  | 'SEND_DIRECT_MESSAGE'
  | 'CREATE_CUSTOM_ROLE'
  | 'SEND_EMAIL_TO_USERS'
  | 'CREATE_GEOGUESSER_GAME'
  | 'JOIN_GEOGUESSER_GAME'
  | 'MAKE_GUESS_GEOGUESSER_GAME'
  | 'MAKE_RATING'
  | 'UPLOAD_FILE'
  | 'DELETE_ACCOUNT'
  | 'MAKE_ROLE'
  | 'ASSIGN_ROLE'
  | 'VIEW_ROLES'
  | 'EDIT_ROLE'
  | 'GENERATE_AGORA_TOKEN'
  | 'REFRESH_AGORA_TOKEN'
  | 'CREATE_AMBASSADOR'
  | 'DELETE_AMBASSADOR'
  | 'UPDATE_AMBASSADOR'
  | 'TRANSLATE'
  | 'CREATE_DISCORD_ROLE'
  | 'VIEW_DISCORD_ROLE'
  | 'CREATE_GEOGUESSER_PLACES'
  | 'HYPERBEAM_LIST_SESSIONS'
  | 'HYPERBEAM_DELETE_SESSIONS'
  | 'ADMIN_VIEW_USERS'
  | 'ADMIN_LANGUAGES'
  | 'ADMIN_AGORA_TOKENS_AUDIT'
  | 'ADMIN_ROLE'
  | 'ADMIN_DIRECT_MESSAGE'
  | 'ADMIN_AMBASSADORS'
  | 'ADMIN_DISCORD_ROLES'
  | 'ADMIN_DISCORD_AUDIT_EVENT'
  | 'ADMIN_DISCUSSION_LANGUAGE'
  | 'ADMIN_DISCUSSION_LANGUAGE_AUDIT_EVENTS'
  | 'ADMIN_USER_FLAGGING'
  | 'ADMIN_GEOGAME_ROUNDS'
  | 'ADMIN_GEOGAME_STATIC_COORDINATES'
  | 'ADMIN_GEOGAME_AUDIT_EVENTS'
  | 'ADMIN_EVENT_GAME_CHAT'
  | 'ADMIN_EVENT_CHAT'
  | 'ADMIN_EVENT_PARTICIPANTS'
  | 'ADMIN_PICTIONARY_AUDIT'
  | 'ADMIN_PICTIONARY_GAMES'
  | 'ADMIN_PICTIONARY_ROUNDS'
  | 'ADMIN_PICTIONARY_GUESS'
  | 'ADMIN_PICTIONARY_WORDS'
  | 'ADMIN_TRANSLATION_AUDIT'
  | 'ADMIN_REDIS_CONSOLE'
  | 'ADMIN_DEBUGGING_CONSOLE'
  | 'CREATE_TRANSCRIPT'
  | 'ADMIN_RECORDINGS'
  | 'HUDDLE_HOST'
export type RolesInDb = {
  id?: number | null
  name?: string | null
  permissions?: Permission[] | null
}
export type PublicUserProfile = {
  id?: number | null
  username?: string | null
  image?: string | null
  full_name?: string | null
  country?: string | null
  pronouns?: Pronouns | null
  profile?: TeacherProfileResponse | null
  accent?: string | null
  voice_gender?: string | null
  learning?: LanguageProficiency[] | null
  speaking?: LanguageProficiency[] | null
  status?: UserStatus
  display_name?: string | null
  is_company?: boolean | null
  cefr_level?: string | null
  roles?: RolesInDb[] | null
  plan?: string | null
}
export type UserRating = {
  rater: PublicUserProfile
  updated_time: string
  created_time: string
  rating: number
  comment?: string | null
}
export const {
  useRatingRateUserMutation,
  useRatingDeleteRatingMutation,
  useRatingChangeRatingForUserMutation,
  useRatingGetRatingIdQuery,
  useLazyRatingGetRatingIdQuery,
  useRatingGetRatingsForUserQuery,
  useLazyRatingGetRatingsForUserQuery,
} = injectedRtkApi
